import { useState } from "react";
import { toastService } from "@services/ToastService";
import { milestoneService } from "@services/MilestoneService";
import { MilestonePayload } from "@shared/interfaces/ProgramFiles";

export const useMilestone = () => {
  const [loading, setLoading] = useState(false);

  const saveMilestone = async (
    payload: MilestonePayload,
    options?: {
      milestoneId?: string;
    }
  ) => {
    setLoading(true);
    try {
      const response = options?.milestoneId
        ? await milestoneService.updateMilestone(options.milestoneId, payload)
        : await milestoneService.createMilestone(payload);

      toastService.success(
        `Milestone ${options?.milestoneId ? "updated" : "created"} successfully`
      );

      return response;
    } catch (error) {
      toastService.error("Failed to save task");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectMilestones = async (
    programId: number,
    projectId: string | number
  ) => {
    setLoading(true);
    try {
      const response = await milestoneService.getProjectMilestones(
        programId,
        projectId
      );
      return response || [];
    } catch (error) {
      console.error("Failed to fetch milestones");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramMilestones = async (
    programId: string | number,
    params?: {
      start_date: string;
      end_date: string;
    }
  ) => {
    setLoading(true);
    try {
      const response = await milestoneService.getProgramMilestones(
        programId,
        params
      );
      return response || [];
    } catch (error) {
      console.error("Failed to fetch milestones");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramLinkedProjects = async (
    programId: string | number,
    params?: {
      offset?: number;
      limit?: number;
    }
  ) => {
    setLoading(true);
    try {
      const response = await milestoneService.getProgramLinkedProjects(
        programId,
        params
      );
      return response || [];
    } catch (error) {
      console.error("Failed to fetch milestones");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteMilestone = async (milestoneId: number | string) => {
    setLoading(true);
    try {
      await milestoneService.deleteMilestone(milestoneId);
      toastService.success("Milestone deleted");
    } catch (error) {
      toastService.error("Failed to delete milestone");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getMilestoneById = async (milestoneId: string | number) => {
    try {
      return await milestoneService.getMilestoneById(milestoneId);
    } catch (error) {
      console.error("Failed to fetch milestone");
      throw error;
    }
  };

  return {
    loading,
    saveMilestone,
    fetchProjectMilestones,
    fetchProgramLinkedProjects,
    fetchProgramMilestones,
    deleteMilestone,
    getMilestoneById,
  };
};
